import { Dispatch, JSX, SetStateAction, useState } from 'react';
import { Route, Switch, useHistory } from 'react-router-dom';
import {
  Box,
  Grid,
  Typography,
  useTheme,
  useMediaQuery,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  Checkbox,
} from '@mui/material';
import COLORS from '@/resources/style/Themes';
import { ErrorStringKeys, StringKeys } from '@/resources/strings/StringKeys';
import { useTranslation } from 'react-i18next';
import {
  Add,
  DeleteOutlined,
  MoreVert,
  PersonRemove as PersonRemoveIcon,
} from '@mui/icons-material';
import InfoModal from '@/components/modals/utils/InfoModal';
import { updateGroupLocationTrackingViaAdmin } from '@/repository/OrganizationDAO';
import {
  ToastType,
  useToast,
} from '@/context_providers/toast_context/ToastContext';
import LoadingData from '@/components/loading/LoadingData';
import { OrganizationOnAzure, SubscriptionTier } from '../../../../generated-client/models';
import Group from '@/models/Organization/Group';
import { OrgUser } from '@/models/User/OrgUser';
import GroupEmergencyResponseTeam from '../emergency_responder_management/GroupEmergencyResponders';
import { getGroupTierAuthIds } from '@/utils/organization/getGroupTierAuthIds';
import {
  buildPoolCounts,
  getPoolPurchased,
} from '@/utils/organization/orgSeatAvailability';
import { getTierLabelKey } from '@/utils/entitlements/tierLabels';
import { Routes } from '@/enums/Routes';
import AllUsersPage from './AllUsersPage';
import OrgFreeTrialAlert from './OrgFreeTrialAlert';
import { useBannerHeight } from '@/context_providers/banner_context/BannerHeightContext';

type GroupRouteProps = {
  allGroups: Group[];
  renderGroup: (
    group_name: string,
    group_value: OrgUser[],
    paidTierUserAuthIds: string[],
    connectTierUserAuthIds: string[],
  ) => JSX.Element;
  organization: OrganizationOnAzure;
  setShowCreateNewGroupModal: Dispatch<SetStateAction<boolean>>;
  deleteGroup: (groupName: string) => Promise<void>;
  orgUser: OrgUser | null;
  userToHighlight: string | null;
  setUserToHighlight: Dispatch<SetStateAction<string | null>>;
  setConfirmTitle: (title: string) => void;
  setConfirmMessage: (message: string) => void;
  setSelectedUser: (user: OrgUser) => void;
  setShowConfirmModal: (show: boolean) => void;
};

const GroupRoute = ({
  allGroups,
  renderGroup,
  organization,
  setShowCreateNewGroupModal,
  deleteGroup,
  orgUser,
  userToHighlight,
  setUserToHighlight,
  setConfirmTitle,
  setConfirmMessage,
  setSelectedUser,
  setShowConfirmModal,
}: GroupRouteProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { defaultPageBoxPadding = 10 } = useBannerHeight() ?? {};
  const [infoMessage, setInfoMessage] = useState('');
  const [infoTitle, setInfoTitle] = useState('');
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState<Element | null>(null);
  const handleMenuClick = (event: React.MouseEvent) =>
    setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const { showToast } = useToast();
  const [loadingAPIData, setLoadingAPIData] = useState(false);
  const history = useHistory();

  const handleDeleteGroup = (
    group_name: string,
    paidTierUserAuthIds: string[],
    connectTierUserAuthIds: string[],
  ) => {
    if (paidTierUserAuthIds.concat(connectTierUserAuthIds).length > 0) {
      setInfoMessage(
        t(StringKeys.GROUP_HAS_USERS_ASSIGNED_TO_IT, {
          group: `${group_name}`,
        }),
      );
      setInfoTitle(t(StringKeys.GROUP_HAS_ASSIGNED_USERS));
      setShowInfoModal(true);
      return;
    }
    deleteGroup(group_name);
  };

  const handleAllowGroupLocationTracking = async (
    group_name: string,
    allow_group_location_tracking: boolean,
  ) => {
    try {
      setLoadingAPIData(true);
      const jsonObject = {
        org_code: organization.org_code,
        group_name: group_name,
        allow_group_location_tracking: allow_group_location_tracking,
        organization_azure_id: organization.id,
      };
      await updateGroupLocationTrackingViaAdmin(jsonObject);

      showToast(
        t(StringKeys.LOCATION_SHARING_UPDATED_FOR_X, { x: group_name }),
        ToastType.SUCCESS,
      );
    } catch {
      showToast(
        t(ErrorStringKeys.FAILED_TO_UPDATE_LOCATION_SHARING_FOR_X, {
          x: group_name,
        }),
        ToastType.DANGER,
      );
    } finally {
      setLoadingAPIData(false);
    }
  };

  // Pools are org-wide (shared across all groups), not per-group — purchased
  // and org-wide-used counts are the same for every group's Route below.
  const orgPoolCounts = buildPoolCounts(organization);
  const orgOpsPurchased = getPoolPurchased(orgPoolCounts, SubscriptionTier.Ops);
  const orgRespondPurchased = getPoolPurchased(orgPoolCounts, SubscriptionTier.Respond);
  const opsLabel = getTierLabelKey(SubscriptionTier.Ops);
  const respondLabel = getTierLabelKey(SubscriptionTier.Respond);

  return (
    <>
      <Switch>
        <Route path={Routes.USER_MANAGEMENT_ALL_USERS} exact>
          <Box
            sx={{
              width: '100%',
              position: 'relative',
              zIndex: 1,
              ...(isMobile
                ? { px: 2, pt: 1, mt: 6 }
                : {
                    pl: '260px',
                    pr: '20px',
                    pt: 1,
                    pb: '20px',
                    boxSizing: 'border-box',
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                    overflow: 'hidden',
                    height: (muiTheme) =>
                      `calc(100dvh - ${muiTheme.spacing(defaultPageBoxPadding)})`,
                  }),
            }}
          >
            <OrgFreeTrialAlert organization={organization} />
            <AllUsersPage
              allGroups={allGroups}
              organization={organization}
              orgUser={orgUser}
              userToHighlight={userToHighlight}
              setUserToHighlight={setUserToHighlight}
              setConfirmTitle={setConfirmTitle}
              setConfirmMessage={setConfirmMessage}
              setSelectedUser={setSelectedUser}
              setShowConfirmModal={setShowConfirmModal}
            />
          </Box>
        </Route>
        {allGroups.map(
          ({
            group_id,
            group_name,
            group_value,
            connect_user_auth_ids,
            ops_user_auth_ids,
            respond_user_auth_ids,
            allow_group_location_tracking,
          }) => {
            const { paidTierUserAuthIds, connectTierUserAuthIds } =
              getGroupTierAuthIds({
                connect_user_auth_ids,
                ops_user_auth_ids,
                respond_user_auth_ids,
              });
            // This group's own usage within each org-wide pool.
            const opsUsedInGroup = (ops_user_auth_ids ?? []).length;
            const respondUsedInGroup = (respond_user_auth_ids ?? []).length;
            return (
              <Route
                key={group_name}
                path={`/UserManagement/${group_name}`}
                exact
              >
                <Box
                  sx={{
                    width: '100%',
                    position: 'relative',
                    zIndex: 1,
                    ...(isMobile
                      ? { px: 2, pt: 1, mt: 6 }
                      : {
                          pl: '260px',
                          pr: '20px',
                          boxSizing: 'border-box',
                          display: 'flex',
                          flexDirection: 'column',
                          minHeight: 0,
                          overflow: 'hidden',
                          height: (muiTheme) =>
                            `calc(100dvh - ${muiTheme.spacing(defaultPageBoxPadding)})`,
                        }),
                  }}
                >
                  <OrgFreeTrialAlert organization={organization} />
                  <Grid container spacing={2} sx={{ width: '100%', flexShrink: 0 }}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          mt: 3,
                        }}
                      >
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
                        >
                          <Box
                            sx={{
                              position: 'relative',
                              display: 'inline-block',
                            }}
                          >
                            <Typography
                              sx={{
                                position: 'absolute',
                                top: -12,
                                ml: '2px',
                                fontWeight: 500,
                                fontSize: 15,
                                color: COLORS.TEXT_HEADER_GRAY,
                                textTransform: 'capitalize',
                                lineHeight: 1,
                              }}
                            >
                              {t(StringKeys.GROUP)}
                            </Typography>

                            <Typography
                              variant="h4"
                              component="h1"
                              sx={{
                                color: COLORS.SAFEGUARD_BLUE,
                                fontWeight: 400,
                              }}
                            >
                              {group_name}
                            </Typography>
                          </Box>

                          {group_name !== 'Default' && (
                            <IconButton
                              size="small"
                              sx={{
                                color: COLORS.SAFEGUARD_BLUE,
                                transition:
                                  'color 0.3s ease, transform 0.3s ease',
                                boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.2)',
                                '&:hover': {
                                  color: COLORS.BORDER_RED,
                                  transform: 'translateY(-2px)',
                                },
                              }}
                              onClick={() =>
                                handleDeleteGroup(
                                  group_name,
                                  paidTierUserAuthIds,
                                  connectTierUserAuthIds,
                                )
                              }
                            >
                              <DeleteOutlined sx={{ fontSize: 20 }} />
                            </IconButton>
                          )}
                        </Box>

                        {isMobile && (
                          <IconButton onClick={handleMenuClick}>
                            <MoreVert />
                          </IconButton>
                        )}
                      </Box>

                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1,
                          mt: 1,
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                            alignItems: 'flex-start',
                          }}
                        >
                          {/* Subscriptions used */}
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <PersonRemoveIcon
                              sx={{
                                color: COLORS.SAFEGUARD_BLUE,
                                fontSize: 20,
                                mr: 1.5,
                                mt: '-2px',
                              }}
                            />

                            <Typography
                              sx={{
                                fontSize: 15,
                                fontWeight: 400,
                                color: COLORS.TEXT_HEADER_GRAY,
                              }}
                            >
                              {t(StringKeys.SUBSCRIPTIONS_USED_IN_GROUP, {
                                group: group_name,
                              })}
                              <Box
                                component="span"
                                sx={{
                                  ml: 1,
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 0.75,
                                }}
                              >
                                <Box
                                  component="span"
                                  sx={{
                                    color: COLORS.SAFEGUARD_BLUE,
                                    fontWeight: 500,
                                  }}
                                >
                                  {opsLabel}
                                </Box>
                                <Box
                                  component="span"
                                  sx={{
                                    display: 'inline-block',
                                    px: 1.5,
                                    py: 0.125,
                                    borderRadius: 2,
                                    backgroundColor:
                                      COLORS.BUTTON_BACKGROUND_BLUE,
                                    color: COLORS.SAFEGUARD_BLUE,
                                    fontWeight: 500,
                                  }}
                                >
                                  {opsUsedInGroup}
                                </Box>
                              </Box>
                              <Box
                                component="span"
                                sx={{
                                  ml: 1.5,
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 0.75,
                                }}
                              >
                                <Box
                                  component="span"
                                  sx={{
                                    color: COLORS.SAFEGUARD_BLUE,
                                    fontWeight: 500,
                                  }}
                                >
                                  {respondLabel}
                                </Box>
                                <Box
                                  component="span"
                                  sx={{
                                    display: 'inline-block',
                                    px: 1.5,
                                    py: 0.125,
                                    borderRadius: 2,
                                    backgroundColor:
                                      COLORS.BUTTON_BACKGROUND_GREEN,
                                    color: COLORS.DARK_GREEN,
                                    fontWeight: 500,
                                  }}
                                >
                                  {respondUsedInGroup}
                                </Box>
                              </Box>
                            </Typography>
                          </Box>

                          {/* Location sharing */}
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              ml: '-2px',
                            }}
                          >
                            <Checkbox
                              sx={{
                                p: 0,
                                mr: 1,
                                '&.Mui-checked': { color: COLORS.BLUE_CRAYOLA },
                              }}
                              onChange={() =>
                                handleAllowGroupLocationTracking(
                                  group_name,
                                  !allow_group_location_tracking,
                                )
                              }
                              checked={allow_group_location_tracking || false}
                            />

                            <Typography sx={{ fontWeight: 400, fontSize: 15 }}>
                              <Box
                                component="span"
                                sx={{
                                  color: COLORS.TEXT_HEADER_GRAY,
                                }}
                              >
                                {t(StringKeys.ENABLE_LOCATION_SHARING_FOR)}{' '}
                              </Box>

                              <Box
                                component="span"
                                sx={{
                                  color: COLORS.SAFEGUARD_BLUE,
                                  fontWeight: 500,
                                }}
                              >
                                {group_name}
                              </Box>
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }}>
                      {isMobile ? (
                        <Typography
                          sx={{
                            fontSize: 15,
                            fontWeight: 400,
                            color: COLORS.TEXT_HEADER_GRAY,
                            mt: 1,
                          }}
                        >
                          {t(StringKeys.ORGANIZATION_TOTAL_SUBSCRIPTIONS, {
                            organization: `${organization?.company_name}`,
                          })}
                          <Box
                            component="span"
                            sx={{
                              ml: 1,
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.75,
                            }}
                          >
                            <Box
                              component="span"
                              sx={{
                                color: COLORS.SAFEGUARD_BLUE,
                                fontWeight: 500,
                              }}
                            >
                              {opsLabel}
                            </Box>
                            <Box
                              component="span"
                              sx={{
                                display: 'inline-block',
                                px: 1.5,
                                py: 0.125,
                                borderRadius: 2,
                                backgroundColor: COLORS.BUTTON_BACKGROUND_BLUE,
                                color: COLORS.SAFEGUARD_BLUE,
                                fontWeight: 500,
                              }}
                            >
                              {orgOpsPurchased}
                            </Box>
                          </Box>
                          <Box
                            component="span"
                            sx={{
                              ml: 1.5,
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.75,
                            }}
                          >
                            <Box
                              component="span"
                              sx={{
                                color: COLORS.SAFEGUARD_BLUE,
                                fontWeight: 500,
                              }}
                            >
                              {respondLabel}
                            </Box>
                            <Box
                              component="span"
                              sx={{
                                display: 'inline-block',
                                px: 1.5,
                                py: 0.125,
                                borderRadius: 2,
                                backgroundColor: COLORS.BUTTON_BACKGROUND_GREEN,
                                color: COLORS.DARK_GREEN,
                                fontWeight: 500,
                              }}
                            >
                              {orgRespondPurchased}
                            </Box>
                          </Box>
                        </Typography>
                      ) : (
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-end',
                            mt: 0,
                          }}
                        >
                          {/* Organization Total Subscriptions Desktop */}
                          <Typography
                            sx={{
                              fontSize: 15,
                              fontWeight: 500,
                              color: COLORS.TEXT_HEADER_GRAY,
                              mb: 1,
                            }}
                          >
                            {t(StringKeys.ORGANIZATION_TOTAL_SUBSCRIPTIONS)}
                            <Box
                              component="span"
                              sx={{
                                ml: 1,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.75,
                              }}
                            >
                              <Box
                                component="span"
                                sx={{
                                  color: COLORS.SAFEGUARD_BLUE,
                                  fontWeight: 700,
                                }}
                              >
                                {opsLabel}
                              </Box>
                              <Box
                                component="span"
                                sx={{
                                  display: 'inline-block',
                                  px: 1.5,
                                  py: 0.125,
                                  borderRadius: 2,
                                  backgroundColor:
                                    COLORS.BUTTON_BACKGROUND_BLUE,
                                  color: COLORS.SAFEGUARD_BLUE,
                                  fontWeight: 500,
                                }}
                              >
                                {orgOpsPurchased}
                              </Box>
                            </Box>
                            <Box
                              component="span"
                              sx={{
                                ml: 1.5,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.75,
                              }}
                            >
                              <Box
                                component="span"
                                sx={{
                                  color: COLORS.SAFEGUARD_BLUE,
                                  fontWeight: 700,
                                }}
                              >
                                {respondLabel}
                              </Box>
                              <Box
                                component="span"
                                sx={{
                                  display: 'inline-block',
                                  px: 1.5,
                                  py: 0.125,
                                  borderRadius: 2,
                                  backgroundColor:
                                    COLORS.BUTTON_BACKGROUND_GREEN,
                                  color: COLORS.DARK_GREEN,
                                  fontWeight: 500,
                                }}
                              >
                                {orgRespondPurchased}
                              </Box>
                            </Box>
                          </Typography>
                        </Box>
                      )}
                    </Grid>
                  </Grid>

                  <Box
                    sx={{
                      flex: isMobile ? undefined : 1,
                      minHeight: isMobile ? undefined : 0,
                      display: 'flex',
                      flexDirection: 'column',
                      overflow: isMobile ? undefined : 'hidden',
                    }}
                  >
                    {renderGroup(
                      group_name,
                      group_value,
                      paidTierUserAuthIds,
                      connectTierUserAuthIds,
                    )}
                  </Box>

                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                  >
                    <MenuItem
                      onClick={() => {
                        setShowCreateNewGroupModal(true);
                        handleMenuClose();
                      }}
                    >
                      <ListItemIcon>
                        <Add />
                      </ListItemIcon>
                      {t(StringKeys.ADD_GROUP)}
                    </MenuItem>
                  </Menu>
                </Box>
              </Route>
            );
          },
        )}
      </Switch>
      <InfoModal
        show={showInfoModal}
        onHide={() => setShowInfoModal(false)}
        infoMessage={infoMessage}
        infoTitle={infoTitle}
      />
      <LoadingData show={loadingAPIData} />
    </>
  );
};

export default GroupRoute;
